import { useCallback } from 'react';

import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FieldsetCard } from '@/components/atoms/FieldsetCard/FieldsetCard';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { useBilling } from '@/modules/billing/hooks/useBilling';
import { isRequired } from '@/util/validation/constants';

type FormValues = { invoiceRecipientEmail: string };
const FORM_ID = 'invoice-recipient-form';

export const InvoiceRecipientFieldset = () => {
  const { t } = useTranslation();
  const { customer, updateCustomer, updateCustomerStatus } = useBilling();

  const form = useForm<FormValues>({
    mode: 'onChange',
    values: { invoiceRecipientEmail: customer?.email ?? '' },
  });
  const {
    handleSubmit,
    control,
    formState: { isDirty },
  } = form;

  const onSubmit = useCallback(
    async ({ invoiceRecipientEmail }: FormValues) =>
      updateCustomer({ email: invoiceRecipientEmail }),
    [updateCustomer]
  );

  return (
    <div className="flex flex-col gap-2">
      <h2 className="px-1.5 text-sm font-medium text-muted-foreground">
        {t('billing.invoice_recipient_title')}
      </h2>
      <Form {...form}>
        <form onSubmit={handleSubmit(onSubmit)} id={FORM_ID}>
          <FieldsetCard
            isDirty={isDirty}
            isLoading={updateCustomerStatus === 'pending'}
            disabled={!isDirty}
            saveButtonLabel={t('billing.invoice_recipient_change_email')}
            formId={FORM_ID}
          >
            <FormField
              control={control}
              name="invoiceRecipientEmail"
              rules={isRequired}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('field.email')}</FormLabel>
                  <FormControl>
                    <Input type="email" {...field} />
                  </FormControl>
                  <FormDescription>{t('billing.invoice_recipient_subtitle')}</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          </FieldsetCard>
        </form>
      </Form>
    </div>
  );
};
